// src/components/WildernessInfoPanel.js
import React from 'react';
import styles from './WildernessInfoPanel.module.css';
import { ItemData } from '../data/ItemData'; // Import ItemData
import { getItemImageSrc } from '../utils/getItemImageSrc'; // Import getItemImageSrc
import { MonsterData, isMonsterUnlocked, getUnlockRequirement } from '../data/monsterData'; // Import real MonsterData
import ItemTooltip from './ItemTooltip';

const WildernessInfoPanel = ({ playerData, selectedMonsterKey, onFightAction, onPlayerAction, activeTab, selectedActivity }) => {
  
  // Helper function to check tool requirements and generate error messages
  const checkToolRequirements = (activityKey, toolType) => {
    if (!toolType) return null;
    
    const mainHandItemId = playerData.equipment?.mainHand;
    const tool = mainHandItemId ? playerData.inventory[mainHandItemId] : null;
    
    if (!tool || typeof tool !== 'object' || tool.toolType !== toolType) {
      const toolDisplayName = toolType === 'fishingRod' ? 'Fishing Rod' : 
                             toolType === 'pickaxe' ? 'Pickaxe' : 
                             toolType === 'axe' ? 'Axe' : toolType;
      return `⚠️ ${toolDisplayName} must be equipped to start this activity`;
    }
    
    if (tool.durability <= 0) {
      return `⚠️ Your ${tool.name} is broken and needs repair`;
    }
    
    return null;
  };

  const CarryCapacityDisplay = () => (
    <div className={styles.carryCapSection}>
      <div className={styles.carryCapCircle}>
        <div className={styles.carryCapProgress} 
             style={{
               background: `conic-gradient(
                 #ff9800 0deg,
                 #ff9800 ${(playerData.currentCarryWeight / playerData.carryCapacity) * 360}deg,
                 #37474f ${(playerData.currentCarryWeight / playerData.carryCapacity) * 360}deg,
                 #37474f 360deg
               )`
             }}>
          <div className={styles.carryCapInner}>
            <span className={styles.carryCapText}>
              {playerData.currentCarryWeight.toFixed(1)}<br/>
              <small>/ {playerData.carryCapacity.toFixed(0)}</small>
            </span>
          </div>
        </div>
      </div>
      <div className={styles.carryCapLabel}>Capacity</div>
    </div>
  );

  const TripHaulDisplay = () => {
    const tripItems = Object.entries(playerData.tripInventory || {}).filter(([key, value]) => {
      return typeof value === 'number' ? value > 0 : (typeof value === 'object' && value !== null);
    });

    return (
      <div className={styles.tripHaulSection}>
        <h4>Trip Inventory</h4>
        <div className={styles.tripInventoryGrid}>
          {tripItems.length > 0 ? (
            tripItems.map(([itemKey, value]) => {
              const baseItem = ItemData[itemKey];
              if (!baseItem) return null;
              
              const displayValue = typeof value === 'number' ? value : 1;
              const itemSrc = getItemImageSrc(baseItem.img);
              
              return (
                <ItemTooltip
                  key={itemKey}
                  itemKey={itemKey}
                  itemInstance={displayValue}
                  showStats={false}
                >
                  <div className={styles.tripInventoryItem}>
                    <img src={itemSrc} alt={baseItem.name} />
                    {displayValue > 1 && <span className={styles.itemQuantity}>{displayValue}</span>}
                  </div>
                </ItemTooltip>
              );
            })
          ) : (
            <p className={styles.emptyInventory}>Trip inventory is empty</p>
          )}
        </div>
      </div>
    );
  };

  const renderGatheringInfo = () => {
    if (!selectedActivity) {
      return (
        <div className={styles.contextualInfo}>
          <h3 className={styles.infoTitle}>🌿 Resource Gathering</h3>
          <div className={styles.infoContent}>
            <p className={styles.emptyMessage}>Select a gathering activity from the left panel to view details and start gathering.</p>
          </div>
        </div>
      );
    }

    const activityConfigs = {
      mineOre: {
        title: '⛏️ Mining',
        description: 'Extract valuable ores and minerals from the earth. Higher mining skill increases efficiency and rare material chances.',
        energy: 15,
        toolType: 'pickaxe',
        resources: [
          { img: '/img/crafting/copperOre.png', name: 'Copper Ore', rarity: 'Common' },
          { img: '/img/crafting/ironOre.png', name: 'Iron Ore', rarity: 'Uncommon' },
          { img: '/img/crafting/stone.png', name: 'Stone', rarity: 'Abundant' }
        ]
      },
      chopWood: {
        title: '🪓 Wood Chopping',
        description: 'Fell trees to gather wood for crafting and construction. Timing and rhythm are key to efficiency.',
        energy: 10,
        toolType: 'axe',
        resources: [
          { img: '/img/crafting/wood.png', name: 'Wood', rarity: 'Common' },
          { img: '/img/crafting/enchantedWood.png', name: 'Enchanted Wood', rarity: 'Rare' }
        ]
      },
      forage: {
        title: '🍄 Foraging',
        description: 'Search for edible plants, herbs, and useful materials. Avoid dangerous items!',
        energy: 5,
        resources: [
          { img: '/img/crafting/herbs.png', name: 'Herbs', rarity: 'Common' },
          { img: '/img/consumables/berries.png', name: 'Berries', rarity: 'Common' },
          { img: '/img/crafting/wildMushroom.png', name: 'Wild Mushrooms', rarity: 'Uncommon' }
        ]
      },
      dungeonMining: {
        title: '⛏️ Dungeon Mines',
        description: 'Venture into the dangerous dungeon mines! Explore procedurally generated floors, fight monsters, and gather valuable resources. Risk vs reward gameplay with increasing difficulty.',
        energy: 0,
        resources: [
          { img: '/img/crafting/copperOre.png', name: 'Copper Ore', rarity: 'Common' },
          { img: '/img/crafting/ironOre.png', name: 'Iron Ore', rarity: 'Uncommon' },
          { img: '/img/crafting/goldOre.png', name: 'Gold Ore', rarity: 'Rare' },
          { img: '/img/crafting/gemShards.png', name: 'Gem Shards', rarity: 'Rare' },
          { img: '/img/crafting/emeraldGem.png', name: 'Emerald', rarity: 'Very Rare' },
          { img: '/img/crafting/rubyGem.png', name: 'Ruby', rarity: 'Very Rare' },
          { img: '/img/crafting/diamondGem.png', name: 'Diamond', rarity: 'Ultra Rare' }
        ]
      }
    };

    const config = activityConfigs[selectedActivity];
    if (!config) {
      return (
        <div className={styles.contextualInfo}>
          <h3 className={styles.infoTitle}>🌿 Resource Gathering</h3>
          <div className={styles.infoContent}>
            <p className={styles.emptyMessage}>Unknown activity selected.</p>
          </div>
        </div>
      );
    }

    return (
      <div className={styles.contextualInfo}>
        <h3 className={styles.infoTitle}>{config.title}</h3>
        <div className={styles.infoContent}>
          <div className={styles.selectedActivityInfo}>
            <div className={styles.compactActivityLayout}>
              <div className={styles.activityDescription}>
                <p>{config.description}</p>
              </div>
              <div className={styles.resourceChances}>
                <h5>Possible Resources:</h5>
                <div className={styles.compactResourceList}>
                  {config.resources.map((resource, index) => (
                    <div key={index} className={styles.resource}>
                      <img src={resource.img} alt={resource.name} />
                      <span>{resource.name} ({resource.rarity})</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            {/* Tool requirement error message */}
            {checkToolRequirements(selectedActivity, config.toolType) && (
              <div className={styles.toolErrorMessage}>
                {checkToolRequirements(selectedActivity, config.toolType)}
              </div>
            )}
            <button 
              onClick={() => onPlayerAction(selectedActivity)}
              className={`${styles.startActivityButton} ${styles.primaryButton}`}
              disabled={playerData.energy.current < config.energy || checkToolRequirements(selectedActivity, config.toolType)}
            >
              {checkToolRequirements(selectedActivity, config.toolType) 
                ? 'Tool Required' 
                : playerData.energy.current < config.energy 
                ? `Need ${config.energy} Energy` 
                : `Start ${config.title.split(' ')[1] || 'Activity'} (${config.energy} Energy)`}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderActivitiesInfo = () => {
    if (!selectedActivity || !['startFishing', 'dungeonMining', 'startFarming'].includes(selectedActivity)) {
      return (
        <div className={styles.contextualInfo}>
          <h3 className={styles.infoTitle}>🎣 Wilderness Activities</h3>
          <div className={styles.infoContent}>
            <p className={styles.emptyMessage}>Select an activity from the left panel to view details and start the activity.</p>
          </div>
        </div>
      );
    }

    const activityConfigs = {
      startFishing: {
        title: '🎣 Fishing',
        description: 'Cast your line into wilderness waters. Use precise timing to hook and reel in various fish species.',
        energy: 0,
        toolType: 'fishingRod',
        resources: [
          { img: '/img/fish/minnow.png', name: 'Minnow', rarity: 'Easy' },
          { img: '/img/fish/sunfish.png', name: 'Sunfish', rarity: 'Easy' },
          { img: '/img/fish/catfish.png', name: 'Catfish', rarity: 'Medium' }
        ],
        guide: {
          title: 'How to Fish:',
          steps: [
            { number: 1, text: 'Wait for bite' },
            { number: 2, text: 'Click to hook' },
            { number: 3, text: 'Keep bobber over fish' },
            { number: 4, text: 'Fill progress bar' }
          ]
        }
      },
      dungeonMining: {
        title: '⛏️ Dungeon Mines',
        description: 'Venture into the dangerous dungeon mines! Explore procedurally generated floors, fight monsters, and gather valuable resources. Risk vs reward gameplay with increasing difficulty.',
        energy: 0,
        toolType: 'pickaxe',
        requiredBuilding: 'dungeonEntrance',
        resources: [
          { img: '/img/crafting/copperOre.png', name: 'Copper Ore', rarity: 'Common' },
          { img: '/img/crafting/ironOre.png', name: 'Iron Ore', rarity: 'Uncommon' },
          { img: '/img/crafting/goldOre.png', name: 'Gold Ore', rarity: 'Rare' },
          { img: '/img/crafting/gemShards.png', name: 'Gem Shards', rarity: 'Rare' },
          { img: '/img/crafting/emeraldGem.png', name: 'Emerald', rarity: 'Very Rare' },
          { img: '/img/crafting/rubyGem.png', name: 'Ruby', rarity: 'Very Rare' },
          { img: '/img/crafting/diamondGem.png', name: 'Diamond', rarity: 'Ultra Rare' }
        ],
        guide: {
          title: 'How to Mine:',
          steps: [
            { number: 1, text: 'Click rocks to mine ore' },
            { number: 2, text: 'Fight monsters for loot' },
            { number: 3, text: 'Find the ladder to descend' },
            { number: 4, text: 'Manage health and energy' }
          ]
        }
      },
      startFarming: {
        title: '🌱 Farming',
        description: 'Tend your own farm plot! Plant seeds, water crops, and harvest produce to create valuable resources.',
        energy: 0,
        requiredBuilding: 'landPlot',
        resources: [
          { img: '/img/consumables/berries.png', name: 'Berries', rarity: 'Common' },
          { img: '/img/crafting/herbs.png', name: 'Herbs', rarity: 'Common' },
          { img: '/img/crafting/wildMushroom.png', name: 'Vegetables', rarity: 'Uncommon' }
        ],
        guide: {
          title: 'How to Farm:',
          steps: [
            { number: 1, text: 'Till soil with hoe' },
            { number: 2, text: 'Plant seeds' },
            { number: 3, text: 'Water your crops' },
            { number: 4, text: 'Harvest when grown' }
          ]
        }
      }
    };

    const config = activityConfigs[selectedActivity];
    const hasValidTool = playerData.equipment?.mainHand && 
                        playerData.inventory[playerData.equipment.mainHand]?.toolType === config.toolType;
    
    // Check if required building is owned for farming
    const hasRequiredBuilding = config.requiredBuilding ? 
                                playerData.buildings?.[config.requiredBuilding]?.owned : true;

    return (
      <div className={styles.contextualInfo}>
        <h3 className={styles.infoTitle}>{config.title}</h3>
        <div className={styles.infoContent}>
          <div className={styles.selectedActivityInfo}>
            <div className={styles.compactActivityLayout}>
              <div className={styles.activityDescription}>
                <p>{config.description}</p>
                <div className={styles.compactFishingGuide}>
                  <h5>{config.guide.title}</h5>
                  <div className={styles.guideSteps}>
                    {config.guide.steps.map((step, index) => (
                      <div key={index} className={styles.guideStep}>
                        <span className={styles.stepNumber}>{step.number}</span>
                        <span>{step.text}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className={styles.resourceChances}>
                <h5>Possible {selectedActivity === 'startFishing' ? 'Catches' : selectedActivity === 'startFarming' ? 'Harvests' : 'Resources'}:</h5>
                <div className={styles.compactResourceList}>
                  {config.resources.map((resource, index) => (
                    <div key={index} className={styles.resource}>
                      <img src={resource.img} alt={resource.name} />
                      <span>{resource.name} ({resource.rarity})</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            {/* Tool requirement or building requirement error message */}
            {(!hasRequiredBuilding && config.requiredBuilding) && (
              <div className={styles.toolErrorMessage}>
                ⚠️ Land Plot building required (Purchase from Shop)
              </div>
            )}
            {(!hasValidTool && config.toolType && hasRequiredBuilding) && (
              <div className={styles.toolErrorMessage}>
                {checkToolRequirements(selectedActivity, config.toolType)}
              </div>
            )}
            <button 
              onClick={() => onPlayerAction(selectedActivity)}
              className={`${styles.startActivityButton} ${styles.primaryButton}`}
              disabled={(!hasValidTool && config.toolType) || (!hasRequiredBuilding && config.requiredBuilding) || (config.energy > 0 && playerData.energy.current < config.energy)}
            >
              {!hasRequiredBuilding && config.requiredBuilding
                ? `Building Required`
                : !hasValidTool && config.toolType
                ? `Tool Required` 
                : config.energy > 0 && playerData.energy.current < config.energy
                ? `Need ${config.energy} Energy`
                : `Start ${config.title.split(' ')[1] || 'Activity'}${config.energy > 0 ? ` (${config.energy} Energy)` : ''}`}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderCombatInfo = () => {
    if (!selectedMonsterKey) {
      return (
        <div className={styles.contextualInfo}>
          <h3 className={styles.infoTitle}>⚔️ Combat Information</h3>
          <div className={styles.infoContent}>
            <p className={styles.emptyMessage}>Select a creature from the combat tab to view its details and engage in battle.</p>
          </div>
        </div>
      );
    }

    const monster = MonsterData[selectedMonsterKey];
    if (!monster) {
      return (
        <div className={styles.contextualInfo}>
          <h3 className={styles.infoTitle}>⚔️ Combat Information</h3>
          <div className={styles.infoContent}>
            <p className={styles.emptyMessage}>Monster data not found.</p>
          </div>
        </div>
      );
    }

    const getDifficultyColor = (tier) => {
      if (tier <= 1) return '#4caf50'; // Green - Easy
      if (tier <= 2) return '#ff9800'; // Orange - Medium
      if (tier <= 3) return '#f44336'; // Red - Hard
      return '#9c27b0'; // Purple - Elite
    };

    const getDifficultyText = (tier) => {
      if (tier <= 1) return 'Easy';
      if (tier <= 2) return 'Medium';
      if (tier <= 3) return 'Hard';
      return 'Elite';
    };

    return (
      <div className={styles.contextualInfo}>
        <div className={styles.infoContent}>
          {/* Main Monster Info Panel */}
          <div className={styles.monsterMainPanel}>
            <div className={styles.monsterImageAndDifficulty}>
              <img 
                src={getItemImageSrc(monster.img)} 
                alt={monster.name} 
                className={styles.largeMonsterImage} 
              />
              <div className={styles.difficultyBadge} style={{ backgroundColor: getDifficultyColor(monster.tier) }}>
                {getDifficultyText(monster.tier)} (T{monster.tier})
              </div>
            </div>
            
            <div className={styles.monsterNameAndStats}>
              <h3 className={styles.monsterName}>{monster.name}</h3>
              <div className={styles.monsterStatsRow}>
                <div className={styles.statCard}>
                  <span className={styles.statIcon}>❤️</span>
                  <span className={styles.statValue}>{monster.stats.health}</span>
                  <span className={styles.statLabel}>HP</span>
                </div>
                <div className={styles.statCard}>
                  <span className={styles.statIcon}>⚔️</span>
                  <span className={styles.statValue}>{monster.stats.minDamage}-{monster.stats.maxDamage}</span>
                  <span className={styles.statLabel}>DMG</span>
                </div>
                <div className={styles.statCard}>
                  <span className={styles.statIcon}>✨</span>
                  <span className={styles.statValue}>{monster.stats.xp}</span>
                  <span className={styles.statLabel}>XP</span>
                </div>
                {monster.stats.defense > 0 && (
                  <div className={styles.statCard}>
                    <span className={styles.statIcon}>🛡️</span>
                    <span className={styles.statValue}>{monster.stats.defense}</span>
                    <span className={styles.statLabel}>DEF</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Description Panel */}
          <div className={styles.descriptionPanel}>
            <h5>Description</h5>
            <p>{monster.description}</p>
          </div>

          {/* Compact Loot Panel */}
          {monster.lootTable && Object.keys(monster.lootTable).length > 0 && (
            <div className={styles.lootPanel}>
              <h5>Drops</h5>
              <div className={styles.compactLootRow}>
                {Object.entries(monster.lootTable).map(([itemKey, lootData]) => {
                  if (itemKey === 'gold') {
                    return (
                      <div key={itemKey} className={styles.miniLootItem} title={`${lootData.quantity[0]}-${lootData.quantity[1]} Gold (${Math.round(lootData.chance * 100)}%)`}>
                        <img src="/img/crafting/goldCoins.png" alt="Gold" />
                        <span>{Math.round(lootData.chance * 100)}%</span>
                      </div>
                    );
                  }
                  const item = ItemData[itemKey];
                  if (!item) return null;
                  return (
                    <div key={itemKey} className={styles.miniLootItem} title={`${item.name} (${Math.round(lootData.chance * 100)}%)`}>
                      <img src={getItemImageSrc(item.img)} alt={item.name} />
                      <span>{Math.round(lootData.chance * 100)}%</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          
          {/* Fight Button or Unlock Requirements */}
          {(() => {
            const playerKillCounts = playerData.monsterKills || {};
            const isUnlocked = isMonsterUnlocked(selectedMonsterKey, playerKillCounts);
            const unlockReq = getUnlockRequirement(selectedMonsterKey);
            
            if (isUnlocked) {
              return (
                <button className={`${styles.fightButton} ${styles.primaryButton}`} onClick={onFightAction}>
                  ⚔️ Fight {monster.name}!
                </button>
              );
            } else {
              return (
                <div className={styles.unlockRequirements}>
                  <div className={styles.lockedMessage}>
                    <span className={styles.lockIcon}>🔒</span>
                    <span>This monster is locked</span>
                  </div>
                  {unlockReq && (
                    <div className={styles.requirementDetails}>
                      <p className={styles.requirementText}>
                        <strong>Unlock by:</strong> Defeat {unlockReq.killCount} {unlockReq.monsterName}
                      </p>
                                             <p className={styles.progressText}>
                         Progress: {playerKillCounts[unlockReq.requiredMonsterKey] || 0} / {unlockReq.killCount}
                       </p>
                    </div>
                  )}
                  <button className={`${styles.fightButton} ${styles.disabledButton}`} disabled>
                    ⚔️ Fight {monster.name}!
                  </button>
                </div>
              );
            }
          })()}
        </div>
      </div>
    );
  };

  const renderContextualContent = () => {
    switch(activeTab) {
      case 'gathering':
        return renderGatheringInfo();
      case 'activities':
        return renderActivitiesInfo();
      case 'combat':
        return renderCombatInfo();
      default:
        return renderGatheringInfo();
    }
  };

  return (
    <div className={styles.infoPanelContainer}>
      <div className={styles.topSectionContainer}>
        <CarryCapacityDisplay />
        <TripHaulDisplay />
      </div>
      {renderContextualContent()}
    </div>
  );
};

export default WildernessInfoPanel;